<script setup lang="ts">
import { computed, ref } from 'vue'
import { BountyResponseStatus } from '@roadfootage/api-client'
import type { OwnerBountyResponseDto } from '@roadfootage/api-client'
import { formatDateTime, formatNumber } from '@/utils/format'
import { bountyResponseDecisionReason } from '@/utils/limits'

const props = defineProps<{
  responses: OwnerBountyResponseDto[]
  loading?: boolean
  error?: boolean
  /** The poster can still accept or reject (the bounty is not claimed). */
  canDecide?: boolean
  /** Shown in the accept confirmation: the points that go to the uploader. */
  rewardPoints?: number
  /** Id of the response a decision is being saved for. */
  deciding?: string | null
  /** Why the last decision failed. */
  decisionError?: string | null
}>()

const emit = defineEmits<{
  accept: [responseId: string]
  reject: [responseId: string, reason: string]
}>()

const statusColor = (status: BountyResponseStatus | undefined) => {
  if (status === BountyResponseStatus.Accepted) return 'green-8'
  if (status === BountyResponseStatus.Rejected) return 'red-8'
  return 'blue-grey-6'
}

const showActions = (response: OwnerBountyResponseDto) =>
  !!props.canDecide && response.status === BountyResponseStatus.Pending

const acceptingId = ref<string | null>(null)
const rejectingId = ref<string | null>(null)
const reason = ref('')
const trimmedReason = computed(() => reason.value.trim())

const acceptOpen = computed({
  get: () => acceptingId.value !== null,
  set: (open: boolean) => {
    if (!open) acceptingId.value = null
  }
})
const rejectOpen = computed({
  get: () => rejectingId.value !== null,
  set: (open: boolean) => {
    if (!open) rejectingId.value = null
  }
})

function askToReject(id: string) {
  reason.value = ''
  rejectingId.value = id
}

function confirmAccept() {
  if (!acceptingId.value) return
  emit('accept', acceptingId.value)
  acceptingId.value = null
}

function confirmReject() {
  if (!rejectingId.value || !trimmedReason.value) return
  emit('reject', rejectingId.value, trimmedReason.value)
  rejectingId.value = null
}
</script>

<template>
  <div>
    <div v-if="loading" class="flex flex-center q-pa-md"><q-spinner /></div>
    <div v-else-if="error" class="text-caption text-negative q-pa-sm">
      Couldn't load responses. Close and reopen this section to try again.
    </div>
    <div v-else-if="!responses.length" class="text-caption text-blue-grey-6 q-pa-sm">
      No responses yet.
    </div>
    <template v-else>
      <q-banner
        v-if="decisionError"
        dense
        rounded
        class="bg-red-1 text-red-9 q-mb-sm"
        data-testid="decision-error"
      >
        {{ decisionError }}
      </q-banner>
      <q-list separator>
        <q-item v-for="response in responses" :key="response.id" class="column q-px-none">
          <div class="row items-center no-wrap q-mb-xs">
            <q-chip
              dense
              size="sm"
              text-color="white"
              :color="statusColor(response.status)"
              data-testid="response-status"
              >{{ response.status }}</q-chip
            >
            <span class="text-caption text-blue-grey-6 q-ml-sm">{{
              formatDateTime(response.createdAt)
            }}</span>
          </div>
          <template v-if="response.preview">
            <video
              v-if="response.preview.previewUrl"
              :src="response.preview.previewUrl"
              :poster="response.preview.thumbnailUrl || undefined"
              controls
              preload="none"
              class="response-media"
            />
            <img
              v-else-if="response.preview.thumbnailUrl"
              :src="response.preview.thumbnailUrl"
              alt="Response preview"
              class="response-media"
            />
            <div
              v-if="response.preview.incidentDescription"
              class="text-caption text-blue-grey-8 q-mt-xs"
            >
              {{ response.preview.incidentDescription }}
            </div>
          </template>
          <div v-else class="text-caption text-blue-grey-6">
            Awaiting moderation, preview not yet available
          </div>
          <div
            v-if="response.status === BountyResponseStatus.Rejected && response.decisionReason"
            class="text-caption text-red-9 q-mt-xs"
            data-testid="decision-reason"
          >
            "{{ response.decisionReason }}"
          </div>
          <div v-if="showActions(response)" class="row justify-end q-gutter-sm q-mt-xs">
            <q-btn
              flat
              dense
              no-caps
              color="red-8"
              label="Reject"
              :disable="!!deciding"
              :data-testid="`reject-${response.id}`"
              @click="askToReject(response.id!)"
            />
            <q-btn
              unelevated
              dense
              no-caps
              color="green-8"
              class="q-px-md"
              label="Accept"
              :disable="!response.preview || !!deciding"
              :loading="deciding === response.id"
              :data-testid="`accept-${response.id}`"
              @click="acceptingId = response.id!"
            />
          </div>
        </q-item>
      </q-list>
    </template>

    <q-dialog v-model="acceptOpen">
      <q-card class="decision-dialog">
        <q-card-section>
          <div class="text-h6">Accept this footage?</div>
          <div class="text-body2 q-mt-sm">
            {{ formatNumber(rewardPoints ?? 0) }} pts go to the uploader. Other responses are
            declined. Can't undo.
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat no-caps label="Cancel" v-close-popup />
          <q-btn
            unelevated
            no-caps
            color="green-8"
            label="Accept"
            data-testid="confirm-accept"
            @click="confirmAccept"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="rejectOpen">
      <q-card class="decision-dialog">
        <q-card-section>
          <div class="text-h6">Reject response</div>
          <q-input
            v-model="reason"
            type="textarea"
            autogrow
            outlined
            class="q-mt-sm"
            label="Reason (required)"
            :maxlength="bountyResponseDecisionReason"
            counter
            data-testid="reject-reason"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat no-caps label="Cancel" v-close-popup />
          <q-btn
            unelevated
            no-caps
            color="red-8"
            label="Reject"
            :disable="!trimmedReason"
            data-testid="confirm-reject"
            @click="confirmReject"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<style lang="scss" scoped>
.response-media {
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: 4px;
  background: #000;
}

.decision-dialog {
  width: 100%;
  max-width: 420px;
}
</style>
